import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Avatar, Button, Card, Col, Divider, Input, InputNumber, Modal, Popconfirm, Row, Select, Space,
  Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  CalendarOutlined, CarOutlined, DeleteOutlined, EditOutlined, FileOutlined, HomeOutlined, PaperClipOutlined,
  PlusOutlined, SwapOutlined, UserOutlined,
} from '@ant-design/icons';
import SoftTag from '../components/SoftTag';
import InfoPill from '../components/InfoPill';
import HeaderField from '../components/HeaderField';
import AttachmentPreview from '../components/AttachmentPreview';
import EntityCommentsCard from '../components/EntityCommentsCard';
import RichTextEditor from '../components/RichTextEditor';
import SupplierComparisonDialog from '../modals/SupplierComparisonDialog';
import SaveAsNewProductModal from '../modals/SaveAsNewProductModal';
import InquiryItemModal from '../modals/InquiryItemModal';
import SupplierQuoteFormPanel from '../components/SupplierQuoteFormPanel';
import {
  QUOTE_STATUSES, PRIORITY_TONES, getQuoteStatusTone, TYPE_TONES,
} from '../constants/statuses';
import {
  computeCustomerPrice, computeMarginPercent, isSalesUser, isSalesOnlyUser,
} from '../utils/inquiryHelpers';
import {
  computeSupplierQuoteTotal, isReadyForOffer, computeTotalLeadTime, getSupplierTypeFields, getSupplierRolesForQuote,
} from '../utils/quoteHelpers';
import { formatDate, formatDateTime } from '../utils/formatters';
import { brandSemantic } from '../config/brand';
import { useI18n } from '../i18n';

const { Text, Title } = Typography;
const { Option } = Select;

const ROLES = ['Product', 'Production', 'Branding', 'Delivery'];

const ROLE_LABELS = {
  Product: 'Product Supplier',
  Production: 'Production Supplier',
  Branding: 'Branding Supplier',
  Delivery: 'Delivery Supplier',
};

// Field names within a quotation object, per role.
const ROLE_FIELDS = {
  Product: { supplierId: 'supplierId', supplierName: 'supplierName', cost: 'baseInternalPrice' },
  Production: { supplierId: 'productionSupplierId', supplierName: 'productionSupplierName', cost: 'productionCost' },
  Branding: { supplierId: 'brandingSupplierId', supplierName: 'brandingSupplierName', cost: 'brandingCost' },
  Delivery: { supplierId: 'deliverySupplierId', supplierName: 'deliverySupplierName', cost: 'deliveryCost' },
};

const sumCostBuckets = (q = {}) => (
  Number(q.baseInternalPrice || 0)
  + Number(q.productionCost || 0)
  + Number(q.brandingCost || 0)
  + Number(q.deliveryCost || 0)
);

const buildQuotationState = (item) => {
  if (item.quotation) {
    const q = { ...item.quotation };
    if (q.margin === undefined || q.margin === null) {
      q.margin = computeMarginPercent(q.baseInternalPrice, q.baseCustomerPrice);
    }
    return q;
  }
  return {
    supplierId: null,
    supplierName: '',
    baseInternalPrice: 0,
    productionSupplierId: null,
    productionSupplierName: '',
    productionCost: 0,
    brandingSupplierId: null,
    brandingSupplierName: '',
    brandingCost: 0,
    deliverySupplierId: null,
    deliverySupplierName: '',
    deliveryCost: 0,
    totalInternalCost: 0,
    baseCustomerPrice: 0,
    margin: 0,
    discount: 0,
    preliminaryDeliveryTime: '',
    notes: '',
  };
};

// ── Read-only multi-line field rendered as a soft grey box ──────────────────
function GreyBox({ label, value, html, children }) {
  return (
    <div>
      <Text style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#7A869A', letterSpacing: 0.3, marginBottom: 6, textTransform: 'uppercase' }}>
        {label}
      </Text>
      <div style={{
        background: brandSemantic.layout,
        border: `1px solid ${brandSemantic.border}`,
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 13,
        minHeight: 36,
      }}
      >
        {children !== undefined ? children : html ? (
          <div
            className="rich-text-editor-content"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
          />
        ) : (value || <span style={{ color: '#bfbfbf' }}>—</span>)}
      </div>
    </div>
  );
}

const getAvatarTone = (index) => ([
  brandSemantic.primary, brandSemantic.info, brandSemantic.success, brandSemantic.warning, brandSemantic.secondary, brandSemantic.error,
][index % 6]);

const getInitials = (name = '') => (
  name.split(' ').filter(Boolean).slice(0, 2).map((word) => word[0]).join('').toUpperCase()
);

const formatDays = (value, t) => (
  value != null ? `${value} ${value === 1 ? t('Day') : t('Days')}` : <span style={{ color: '#bfbfbf' }}>—</span>
);

// ── Quote status selector styled like existing StatusSelect ─────────────────
function QuoteStatusSelect({ value, onChange, disabled = false }) {
  const { t } = useI18n();
  const tone = getQuoteStatusTone(value);
  return (
    <Select
      value={value}
      onChange={onChange}
      disabled={disabled}
      size="small"
      variant="borderless"
      className="status-tag-select"
      style={{
        width: Math.min(Math.max((value || '').length * 7 + 34, 130), 280),
        '--status-color': tone.color,
        '--status-bg': tone.background,
        '--status-border': tone.border,
      }}
      dropdownMatchSelectWidth={false}
    >
      {QUOTE_STATUSES.map((s) => {
        const st = getQuoteStatusTone(s);
        return (
          <Option key={s} value={s}>
            <span style={{ color: st.color, fontWeight: 500 }}>{t(s)}</span>
          </Option>
        );
      })}
    </Select>
  );
}

// ── Main QuoteDetailPage ─────────────────────────────────────────────────────
export default function QuoteDetailPage({
  quote,
  leads = [],
  products = [],
  categories = [],
  services = [],
  logisticsItems = [],
  suppliers = [],
  supplierProducts = [],
  certificates = [],
  comments = [],
  currentUser,
  onBack,
  onUpdateQuote,
  onSaveQuotation,
  onConvertToOffer,
  onOpenConvertedOffer,
  onOpenSourceInquiry,
  onAddComment,
  onSaveNewProduct,
  onSaveSupplierQuote,
  onSetQuoteStatus,
  onConnectToOffer,
  onCreateOfferFromQuote,
  offers = [],
  canDelete = true,
  t: tProp,
}) {
  const { t: tHook } = useI18n();
  const t = tProp || tHook;

  const quoteLineType = quote.lineType || 'Product';
  const isProductQuote = quoteLineType === 'Product';

  // per-item quotation state: { [itemId]: ItemQuotation }
  const [quotations, setQuotations] = useState(() => {
    const map = {};
    (quote?.items || []).forEach((item) => {
      map[item.id] = buildQuotationState(item);
    });
    return map;
  });

  const [comparisonItem, setComparisonItem] = useState(null);
  const [comparisonRole, setComparisonRole] = useState('Product');
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [saveProductItem, setSaveProductItem] = useState(null);
  const [saveProductOpen, setSaveProductOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingSupplierQuote, setEditingSupplierQuote] = useState(null);

  // Full reset only when navigating to a *different* quote — a status-only
  // patch (e.g. the auto-advance to "Quotation in Process" below) re-creates
  // the `quote` object but keeps the same `items` array reference, so it
  // must not wipe out quotation edits that haven't been saved yet.
  useEffect(() => {
    const map = {};
    (quote?.items || []).forEach((item) => {
      map[item.id] = buildQuotationState(item);
    });
    setQuotations(map);
    setComparisonItem(null);
    setComparisonOpen(false);
    setSaveProductItem(null);
    setSaveProductOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id]);

  // Keep the quotations map in sync when items are added/removed on the
  // *same* quote, without clobbering any in-progress edits on items that
  // still exist.
  useEffect(() => {
    setQuotations((prev) => {
      const next = {};
      (quote?.items || []).forEach((item) => {
        next[item.id] = prev[item.id] || buildQuotationState(item);
      });
      return next;
    });
  }, [quote?.items]);

  const [connectOfferId, setConnectOfferId] = useState(null);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('supplierQuotes');
  const [supplierQuoteStatus, setSupplierQuoteStatus] = useState(() => {
    const initial = {};
    (quote.suppliers || []).forEach((supplier) => {
      const supplierData = quote.supplierQuotes?.[supplier.supplierId];
      initial[supplier.supplierId] = supplierData?.status || null;
    });
    return initial;
  });

  if (!quote) return null;

  // New one-product-per-Quote pipeline (procurement-workflow-redesign):
  // these Quotes carry a `suppliers` array (inherited from the originating
  // Inquiry_Item's Selected Suppliers) instead of a multi-item `items[]`.
  const isNewPipeline = Array.isArray(quote.suppliers);

  const lead = leads.find((l) => l.id === quote.clientId) || null;
  const priorityTone = PRIORITY_TONES[quote.priority] || PRIORITY_TONES.Medium;
  const isSales = isSalesUser(currentUser);
  const isSalesOnly = isSalesOnlyUser(currentUser);
  const getSupplierDisplayName = (supplier, index) => (isSalesOnly ? `${t('Supplier')} ${index + 1}` : supplier.supplierName);
  const isConverted = Boolean(quote.convertedOfferId);
  const canConvert = isSales && quote.status === 'Sent to Sales Quote' && !isConverted;
  const canEdit = !isConverted;

  const getItemCustomerUnitPrice = (quotation = {}) => {
    const beforeDiscount = Number(quotation.baseCustomerPrice || 0)
      + Number(quotation.productionCost || 0)
      + Number(quotation.brandingCost || 0)
      + Number(quotation.deliveryCost || 0);
    return beforeDiscount * (1 - Number(quotation.discount || 0) / 100);
  };

  const getItemCustomerTotal = (item, quotation = {}) => (
    getItemCustomerUnitPrice(quotation) * Number(item.quantity || 0)
  );

  const getItemInternalTotal = (item, quotation = {}) => (
    sumCostBuckets(quotation) * Number(item.quantity || 0)
  );

  // update a single quotation field for an item, recomputing dependent values
  const updateQuotationField = (itemId, field, value) => {
    setQuotations((prev) => {
      const existing = prev[itemId] || {};
      const updated = { ...existing, [field]: value };
      updated.totalInternalCost = sumCostBuckets(updated);
      if (field === 'margin') {
        updated.baseCustomerPrice = computeCustomerPrice(updated.baseInternalPrice, value);
      } else if (field === 'baseCustomerPrice') {
        updated.margin = computeMarginPercent(updated.baseInternalPrice, value);
      } else if (field === 'baseInternalPrice') {
        updated.baseCustomerPrice = computeCustomerPrice(value, updated.margin);
      }
      return { ...prev, [itemId]: updated };
    });
  };

  const openComparisonDialog = (item, role) => {
    setComparisonItem(item);
    setComparisonRole(role);
    setComparisonOpen(true);
  };

  const handleAssignSupplier = (role, { supplierId, supplierName, price }) => {
    if (!comparisonItem) return;
    const itemId = comparisonItem.id;
    const fields = ROLE_FIELDS[role];
    setQuotations((prev) => {
      const existing = prev[itemId] || {};
      const updated = {
        ...existing,
        [fields.supplierId]: supplierId,
        [fields.supplierName]: supplierName,
        [fields.cost]: price,
      };
      if (role === 'Product') {
        updated.baseCustomerPrice = price;
        updated.margin = 0;
      }
      updated.totalInternalCost = sumCostBuckets(updated);
      return { ...prev, [itemId]: updated };
    });
    setComparisonOpen(false);
    setComparisonItem(null);
    if (role === 'Product' && quote.status === 'Ready for Procurement') {
      onUpdateQuote?.(quote.id, { status: 'Quotation in Process' });
    }
  };

  const clearRoleSupplier = (itemId, role) => {
    const fields = ROLE_FIELDS[role];
    setQuotations((prev) => {
      const existing = prev[itemId] || {};
      const updated = {
        ...existing,
        [fields.supplierId]: null,
        [fields.supplierName]: '',
        [fields.cost]: 0,
      };
      updated.totalInternalCost = sumCostBuckets(updated);
      return { ...prev, [itemId]: updated };
    });
  };

  // Supplier ids already assigned to any role on a *different* item in this quote.
  const getUsedSupplierIds = (excludeItemId) => {
    const used = new Set();
    Object.entries(quotations).forEach(([itemId, q]) => {
      if (itemId === excludeItemId) return;
      ROLES.forEach((role) => {
        const id = q?.[ROLE_FIELDS[role].supplierId];
        if (id) used.add(id);
      });
    });
    return used;
  };

  const handleSaveQuotation = () => {
    onSaveQuotation?.(quote.id, quotations);
    message.success(t('Quotation saved'));
  };

  const handleMarkReadyForOffer = () => {
    const missing = (quote.items || []).filter((item) => !quotations[item.id]?.supplierName);
    if (missing.length > 0) {
      const names = missing.map((i) => i.productName).join(', ');
      message.warning(`${t('Assign a supplier to all items first')}: ${names}`);
      return;
    }
    onUpdateQuote?.(quote.id, { status: 'Ready for Sales Quote' });
    message.success(t('Status updated to: {{status}}', { status: t('Ready for Sales Quote') }));
  };

  const handleSaveItem = (itemData) => {
    const items = quote.items || [];
    const exists = items.some((i) => i.id === itemData.id);
    onUpdateQuote?.(quote.id, {
      items: exists
        ? items.map((i) => (i.id === itemData.id ? itemData : i))
        : [...items, itemData],
    });
    message.success(exists ? t('Item updated') : t('Item added'));
    setItemModalOpen(false);
    setEditingItem(null);
  };

  const handleRemoveItem = (itemId) => {
    onUpdateQuote?.(quote.id, {
      items: (quote.items || []).filter((i) => i.id !== itemId),
    });
    message.success(t('Item removed'));
  };

  // ── Quote Products table columns ─────────────────────────────────────────
  const itemColumns = [
    { title: '#', key: 'index', width: 42, render: (_, __, idx) => idx + 1 },
    { title: t('Category'), dataIndex: 'categoryName', key: 'categoryName', width: 120 },
    {
      title: t('Product / Request'),
      dataIndex: 'productName',
      key: 'productName',
      render: (val, record) => (
        <Space size={6}>
          <span>{val}</span>
          {record.isNewProduct && (
            <SoftTag
              tone={{ color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder }}
              style={{ fontSize: 11 }}
            >
              New
            </SoftTag>
          )}
        </Space>
      ),
    },
    { title: t('Qty'), dataIndex: 'quantity', key: 'quantity', width: 70, align: 'right' },
    {
      title: t('Suppliers'),
      key: 'suppliers',
      width: 220,
      render: (_, record) => {
        const q = quotations[record.id] || {};
        const tags = ROLES
          .map((role) => ({ role, name: q[ROLE_FIELDS[role].supplierName] }))
          .filter((entry) => entry.name);
        if (tags.length === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <Space wrap size={4}>
            {tags.map((entry) => (
              <SoftTag
                key={entry.role}
                tone={{ color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted }}
                style={{ fontSize: 11 }}
              >
                {t(entry.role)}: {entry.name}
              </SoftTag>
            ))}
          </Space>
        );
      },
    },
    { title: t('Branding Description'), dataIndex: 'brandingDescription', key: 'brandingDescription', ellipsis: true },
    { title: t('Delivery Requirements'), dataIndex: 'deliveryRequirements', key: 'deliveryRequirements', ellipsis: true },
    {
      title: t('Files'),
      key: 'files',
      width: 80,
      render: (_, record) => {
        const count = record.files?.length || 0;
        return count > 0 ? <Space size={4}><FileOutlined />{count}</Space> : <span style={{ color: '#bfbfbf' }}>—</span>;
      },
    },
    ...(!isConverted ? [{
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => { setEditingItem(record); setItemModalOpen(true); }}
          />
          <Popconfirm
            title={t('Remove this item from the quote?')}
            okText={t('Remove')}
            cancelText={t('Cancel')}
            onConfirm={() => handleRemoveItem(record.id)}
          >
            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  // ── Expanded row: per-product supplier roles + pricing panel ────────────
  const renderExpandedRow = (record) => {
    const q = quotations[record.id] || {};
    const catalogueProduct = products.find((p) => p.id === record.productId) || null;
    const alreadyInCatalogue = products.some(
      (p) => p.name?.toLowerCase() === record.productName?.toLowerCase(),
    );
    const needsCatalogueEntry = record.isNewProduct && !alreadyInCatalogue;
    const usedSupplierIds = getUsedSupplierIds(record.id);

    return (
      <div style={{ padding: '8px 4px' }}>
        {needsCatalogueEntry ? (
          <Button
            type="link"
            style={{ padding: 0, marginBottom: 16 }}
            onClick={() => { setSaveProductItem(record); setSaveProductOpen(true); }}
            disabled={!canEdit}
          >
            {t('Add to Products')}
          </Button>
        ) : (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {ROLES.map((role) => {
              const fields = ROLE_FIELDS[role];
              const supplierName = q[fields.supplierName];
              return (
                <Col xs={24} sm={12} md={6} key={role}>
                  <div style={{ border: `1px solid ${brandSemantic.borderSecondary}`, borderRadius: 10, padding: 12, height: '100%' }}>
                    <Text style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#7A869A', letterSpacing: 0.3 }}>
                      {t(ROLE_LABELS[role])}
                    </Text>
                    {supplierName ? (
                      <>
                        <div style={{ marginTop: 6, fontWeight: 600 }}>{supplierName}</div>
                        <InputNumber
                          min={0}
                          step={0.01}
                          precision={2}
                          value={q[fields.cost] ?? 0}
                          disabled={!canEdit}
                          onChange={(val) => updateQuotationField(record.id, fields.cost, val ?? 0)}
                          style={{ width: '100%', marginTop: 8 }}
                        />
                        {canEdit && (
                          <Space size={10} style={{ marginTop: 6 }}>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openComparisonDialog(record, role)}>
                              {t('Change')}
                            </Button>
                            <Button type="link" size="small" danger style={{ padding: 0 }} onClick={() => clearRoleSupplier(record.id, role)}>
                              {t('Clear')}
                            </Button>
                          </Space>
                        )}
                      </>
                    ) : (
                      <>
                        <div style={{ marginTop: 6, color: '#bfbfbf' }}>{t('Not assigned')}</div>
                        {canEdit && (
                          <Button size="small" style={{ marginTop: 8 }} onClick={() => openComparisonDialog(record, role)}>
                            {t('Select Supplier')}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        <Row gutter={[16, 12]}>
          <Col xs={12} sm={6} md={4}>
            <HeaderField label={t('Total Internal Cost')}>
              <strong>{Number(sumCostBuckets(q)).toLocaleString('da-DK')} DKK</strong>
            </HeaderField>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <HeaderField label={t('Margin %')}>
              {canEdit ? (
                <InputNumber
                  step={0.1}
                  precision={1}
                  value={q.margin ?? 0}
                  onChange={(val) => updateQuotationField(record.id, 'margin', val ?? 0)}
                  style={{ width: '100%' }}
                />
              ) : (
                <Text>{Number(q.margin || 0).toLocaleString('da-DK')}%</Text>
              )}
            </HeaderField>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <HeaderField label={t('Discount %')}>
              {canEdit ? (
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  precision={0}
                  value={q.discount ?? 0}
                  onChange={(val) => updateQuotationField(record.id, 'discount', val ?? 0)}
                  style={{ width: '100%' }}
                />
              ) : (
                <Text>{Number(q.discount || 0).toLocaleString('da-DK')}%</Text>
              )}
            </HeaderField>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <HeaderField label={t('Customer Unit')}>
              <strong>{getItemCustomerUnitPrice(q).toLocaleString('da-DK')} DKK</strong>
            </HeaderField>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <HeaderField label={t('Total Customer Price')}>
              <strong>{getItemCustomerTotal(record, q).toLocaleString('da-DK')} DKK</strong>
            </HeaderField>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <HeaderField label={t('Preliminary Delivery Time')}>
              {canEdit ? (
                <Input
                  value={q.preliminaryDeliveryTime ?? q.leadTime ?? ''}
                  onChange={(e) => updateQuotationField(record.id, 'preliminaryDeliveryTime', e.target.value)}
                  placeholder={t('one week')}
                />
              ) : (
                <Text>{q.preliminaryDeliveryTime || q.leadTime || <span style={{ color: '#bfbfbf' }}>—</span>}</Text>
              )}
            </HeaderField>
          </Col>
        </Row>

        <div style={{ marginTop: 12 }}>
          <HeaderField label={t('Notes')}>
            {canEdit ? (
              <RichTextEditor
                value={q.notes || ''}
                onChange={(html) => updateQuotationField(record.id, 'notes', html)}
                minHeight={70}
              />
            ) : q.notes ? (
              <div
                className="rich-text-editor-content"
                style={{ fontSize: 14 }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.notes) }}
              />
            ) : (
              <Text type="secondary">—</Text>
            )}
          </HeaderField>
        </div>

        <SupplierComparisonDialog
          open={comparisonOpen && comparisonItem?.id === record.id}
          onCancel={() => { setComparisonOpen(false); setComparisonItem(null); }}
          onSelect={handleAssignSupplier}
          role={comparisonRole}
          product={catalogueProduct}
          suppliers={suppliers}
          supplierProducts={supplierProducts}
          certificates={certificates}
          usedSupplierIds={usedSupplierIds}
        />
      </div>
    );
  };

  // ── New one-product-per-Quote pipeline render ────────────────────────────
  if (isNewPipeline) {
    const quoteReadyForOffer = quote.status === 'Ready for Sales Quote';
    const eligibleOffers = offers.filter((offer) => (
      offer.type === 'Offer' && !offer.archived && offer.clientId === quote.clientId
    ));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Button icon={<HomeOutlined />} onClick={onBack}>
            {t('Back to List')}
          </Button>
          <Space>
            {quoteReadyForOffer && (
              <>
                <Button type="primary" icon={<SwapOutlined />} onClick={() => onCreateOfferFromQuote?.(quote.id)}>
                  {t('Create Sales Quote')}
                </Button>
                <Button icon={<SwapOutlined />} onClick={() => setConnectModalOpen(true)}>
                  {t('Connect to Sales Quote')}
                </Button>
              </>
            )}
          </Space>
        </div>

        <Card style={{ borderRadius: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <Space wrap size={12} align="center">
              <Title level={3} style={{ margin: 0 }}>{quote.id}</Title>
              {quote.inquiryId && (
                <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
                  {t('Created from Inquiry')}:{' '}
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                    onClick={() => onOpenSourceInquiry?.(quote.inquiryId)}
                  >
                    {quote.inquiryId}
                  </Button>
                </Text>
              )}
              {quote.convertedOfferId && (
                <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
                  {t('Converted to Sales Quote')}:{' '}
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                    onClick={() => onOpenConvertedOffer?.(quote.convertedOfferId)}
                  >
                    {quote.convertedOfferId}
                  </Button>
                </Text>
              )}
            </Space>
            <Space size={8}>
              <SoftTag tone={TYPE_TONES.Quote}>{t('Internal Quote')}</SoftTag>
              <QuoteStatusSelect
                value={quote.status}
                onChange={(val) => onSetQuoteStatus?.(quote.id, val)}
                disabled={isConverted}
              />
            </Space>
          </div>

          <Row gutter={[12, 12]} style={{ marginTop: 20 }}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <InfoPill icon={<UserOutlined />} label={t('Client Name')} value={quote.client} />
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <InfoPill icon={<EditOutlined />} label={t('Created By')} value={quote.createdBy || '—'} />
            </Col>
            <Col xs={24} sm={24} md={8} lg={8}>
              <InfoPill icon={<HomeOutlined />} label={t('Delivery Address')} value={quote.deliveryAddress || '—'} />
            </Col>
            <Col xs={12} sm={6} md={6} lg={4}>
              <InfoPill
                icon={<CalendarOutlined />}
                label={t('Deadline')}
                value={quote.neededBefore ? formatDate(quote.neededBefore) : '—'}
                accent={brandSemantic.error}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg={4}>
              <InfoPill
                icon={<CarOutlined />}
                label={t('Requested Delivery')}
                value={quote.requestedDeliveryDate ? formatDate(quote.requestedDeliveryDate) : '—'}
              />
            </Col>
          </Row>
        </Card>

        <Card title={t('Quote Details')} style={{ borderRadius: 16 }}>
          <Row gutter={24}>
            <Col xs={24} md={16}>
              <Row gutter={[24, 16]}>
                {isProductQuote ? (
                  <Col xs={12} sm={8}>
                    <HeaderField label={t('Category')}>{quote.categoryName || quote.productCategory || '—'}</HeaderField>
                  </Col>
                ) : (
                  <Col xs={12} sm={8}>
                    <HeaderField label={t('Line Type')}>{t(quoteLineType)}</HeaderField>
                  </Col>
                )}
                <Col xs={12} sm={8}>
                  <HeaderField label={t('Qty')}>{quote.quantity}</HeaderField>
                </Col>
                <Col xs={12} sm={8}>
                  <HeaderField label={t('Re-order')}>{quote.reorder ? t('Yes') : t('No')}</HeaderField>
                </Col>
                <Col xs={12} sm={12}>
                  <HeaderField label={isProductQuote ? t('Product') : t('Name')}>{quote.productName}</HeaderField>
                </Col>
                {isProductQuote && (
                  <Col xs={12} sm={12}>
                    <HeaderField label={t('Target Price (DKK)')}>
                      {quote.targetPrice != null ? Number(quote.targetPrice).toLocaleString('da-DK') : '—'}
                    </HeaderField>
                  </Col>
                )}
              </Row>
              {isProductQuote && (
              <div style={{ marginTop: 16 }}>
                <GreyBox label={t('Product Description')} value={quote.specifications} />
              </div>
              )}
              <div style={{ marginTop: 12 }}>
                <GreyBox label={t('Branding')} html={quote.brandingDescription} />
              </div>
              <div style={{ marginTop: 12 }}>
                <GreyBox label={t('Special Requirements')} html={quote.deliveryRequirements} />
              </div>
            </Col>
            <Col xs={24} md={8}>
              <Card
                size="small"
                title={(
                  <Space>
                    <PaperClipOutlined />
                    <span>{t('Attachments')}</span>
                  </Space>
                )}
                style={{ height: '100%' }}
              >
                {(quote.files || []).length > 0 ? (
                  <AttachmentPreview files={quote.files} thumbnailSize={120} />
                ) : (
                  <Text type="secondary">{t('No attachments yet')}</Text>
                )}
              </Card>
            </Col>
          </Row>
        </Card>

        <Tabs
          activeKey={activeDetailTab}
          onChange={setActiveDetailTab}
          items={[
            { key: 'supplierQuotes', label: t('Supplier Quotes') },
            { key: 'comments', label: t('Comments to Quote') },
          ]}
        />

        {activeDetailTab === 'supplierQuotes' && (
        <Card
          title={(
            <Space>
              <FileOutlined style={{ color: brandSemantic.primary }} />
              <span style={{ fontSize: 16, fontWeight: 700 }}>{t('Supplier Quotes')}</span>
            </Space>
          )}
          style={{ borderRadius: 16 }}
        >
          {(quote.suppliers || []).length === 0 ? (
            <Text type="secondary">{t('No suppliers were selected on the Inquiry for this product.')}</Text>
          ) : (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {(quote.suppliers || []).map((supplier, supplierIndex) => {
                const supplierData = quote.supplierQuotes?.[supplier.supplierId] || { status: null, lines: [] };
                const supplierLines = supplierData.lines || [];
                // A supplier can hold more than one role on this Quote
                // (Manufacturing/Services/Logistics from the Selection_Wizard)
                // — each gets its own cost-entry section below, with the
                // fields relevant to that role.
                const supplierRoles = getSupplierRolesForQuote(quote, supplier.supplierId, suppliers);
                const roleLines = (role) => supplierLines.filter((line) => (
                  line.role ? line.role === role : supplierRoles.length === 1
                ));
                const supplierCertificates = certificates.filter((certificate) => (
                  String(certificate.supplierId) === String(supplier.supplierId)
                  && (certificate.productId == null || certificate.productId === quote.productId)
                ));
                const isEditingSupplier = editingSupplierQuote?.supplierId === supplier.supplierId;
                const quoteStatus = supplierQuoteStatus[supplier.supplierId];
                const isAccepted = quoteStatus === 'accepted';
                const isRejected = quoteStatus === 'rejected';
                const valueOrDash = (value) => (value != null && value !== '' ? value : <span style={{ color: '#bfbfbf' }}>—</span>);

                let cardBorderStyle = { borderColor: brandSemantic.border };
                if (isAccepted) {
                  cardBorderStyle = { borderColor: brandSemantic.success, borderWidth: 2 };
                } else if (isRejected) {
                  cardBorderStyle = { borderColor: brandSemantic.error, borderWidth: 2 };
                }

                return (
                  <Card
                    key={supplier.supplierId}
                    size="small"
                    style={{ width: '100%', ...cardBorderStyle, transition: 'all 0.3s ease', cursor: 'default' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                      <Space align="start" size={12}>
                        <Avatar style={{ backgroundColor: getAvatarTone(supplierIndex), flexShrink: 0 }}>
                          {getInitials(getSupplierDisplayName(supplier, supplierIndex))}
                        </Avatar>
                        <div>
                          <Space size={8} align="center">
                            <Text strong style={{ fontSize: 16 }}>{getSupplierDisplayName(supplier, supplierIndex)}</Text>
                            {supplierRoles.map((role) => (
                              <Tag key={role} style={{ fontSize: 11, margin: 0 }}>{t(role)}</Tag>
                            ))}
                          </Space>
                          {isAccepted && (
                            <Tag color="green" style={{ marginTop: 8, display: 'block', width: 'fit-content' }}>Accepted</Tag>
                          )}
                          {isRejected && (
                            <Tag color="red" style={{ marginTop: 8, display: 'block', width: 'fit-content' }}>Rejected</Tag>
                          )}
                        </div>
                      </Space>
                      {!isEditingSupplier && (
                        <Space>
                          <Button icon={<EditOutlined />} onClick={() => setEditingSupplierQuote(supplier)}>{t('Edit')}</Button>
                          <Button
                            danger={isRejected}
                            onClick={() => {
                              setSupplierQuoteStatus((prev) => ({
                                ...prev,
                                [supplier.supplierId]: isRejected ? null : 'rejected'
                              }));
                              if (!isRejected) {
                                onSaveSupplierQuote?.(quote.id, supplier.supplierId, { status: 'rejected' });
                              }
                            }}
                          >
                            {t('Reject')}
                          </Button>
                          <Button
                            type={isAccepted ? 'primary' : 'default'}
                            onClick={() => {
                              setSupplierQuoteStatus((prev) => ({
                                ...prev,
                                [supplier.supplierId]: isAccepted ? null : 'accepted'
                              }));
                              if (!isAccepted) {
                                onSaveSupplierQuote?.(quote.id, supplier.supplierId, { status: 'accepted' });
                              }
                            }}
                            style={isAccepted ? { backgroundColor: brandSemantic.success } : {}}
                          >
                            {t('Accept')}
                          </Button>
                        </Space>
                      )}
                    </div>

                    {isEditingSupplier ? (
                      <SupplierQuoteFormPanel
                        supplierId={supplier.supplierId}
                        data={supplierData}
                        quantity={quote.quantity}
                        productId={quote.productId}
                        roles={supplierRoles}
                        certificates={certificates}
                        onCancel={() => setEditingSupplierQuote(null)}
                        onSave={(supplierId, data) => {
                          onSaveSupplierQuote?.(quote.id, supplierId, data);
                          setEditingSupplierQuote(null);
                        }}
                      />
                    ) : supplierLines.length === 0 ? (
                      <Text type="secondary">{t('No cost lines entered yet for this supplier.')}</Text>
                    ) : (
                      <Space direction="vertical" size={22} style={{ width: '100%' }}>
                        {supplierRoles.map((role) => {
                          const lines = roleLines(role);
                          if (lines.length === 0) return null;
                          const visibleFields = getSupplierTypeFields(role);
                          const showField = (field) => visibleFields.includes(field);
                          return (
                            <div key={role}>
                              {supplierRoles.length > 1 && (
                                <Tag style={{ marginBottom: 12, fontSize: 12 }} color="processing">{t(role)}</Tag>
                              )}
                              <Space direction="vertical" size={18} style={{ width: '100%' }}>
                                {lines.map((line, lineIndex) => {
                                  const productSubtotal = Number(line.unitPrice || 0) * Number(line.quantity || 0);
                                  return (
                                    <div key={line.id || lineIndex}>
                                      {lines.length > 1 && (
                                        <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13, textTransform: 'uppercase' }}>{t('Proposed Quote')} {lineIndex + 1}</Text>
                                      )}
                                      <Row gutter={[20, 18]}>
                                        {showField('productDetails') && (
                                          <Col xs={24} md={12}>
                                            <GreyBox label={t('Product Details')} html={line.productDetails} />
                                          </Col>
                                        )}
                                        {showField('brandingDecoration') && (
                                          <Col xs={24} md={12}>
                                            <GreyBox label={t('Branding / Decoration')} html={line.brandingDecoration} />
                                          </Col>
                                        )}
                                      </Row>

                                      <Row gutter={[20, 18]} style={{ marginTop: 18 }}>
                                        <Col xs={12} md={4}>
                                          <HeaderField label={t('Requested Qty')}>{Number(quote.quantity || 0).toLocaleString('da-DK')}</HeaderField>
                                        </Col>
                                        <Col xs={12} md={4}>
                                          <HeaderField label={t('Quantity')}><Text strong>{Number(line.quantity || 0).toLocaleString('da-DK')}</Text></HeaderField>
                                        </Col>
                                        {showField('unitPrice') && (
                                          <Col xs={12} md={4}>
                                            <HeaderField label={t('Unit Price')}><Text strong>{valueOrDash(line.unitPrice != null ? `${Number(line.unitPrice).toLocaleString('da-DK')} DKK` : null)}</Text></HeaderField>
                                          </Col>
                                        )}
                                        {showField('productionTime') && (
                                          <Col xs={12} md={4}>
                                            <HeaderField label={t('Production Time')}><Text strong>{formatDays(line.productionTime, t)}</Text></HeaderField>
                                          </Col>
                                        )}
                                        {showField('shippingCost') && (
                                          <Col xs={12} md={4}>
                                            <HeaderField label={t('Shipping Cost')}><Text strong>{valueOrDash(line.shippingCost != null ? `${Number(line.shippingCost).toLocaleString('da-DK')} DKK` : null)}</Text></HeaderField>
                                          </Col>
                                        )}
                                        {showField('validUntil') && (
                                          <Col xs={12} md={4}>
                                            <HeaderField label={t('Valid Until')}><Text strong>{line.validUntil ? formatDate(line.validUntil) : '—'}</Text></HeaderField>
                                          </Col>
                                        )}
                                        {showField('shippingTime') && (
                                          <Col xs={12} md={4}>
                                            <HeaderField label={t('Shipping Time')}><Text strong>{formatDays(line.shippingTime, t)}</Text></HeaderField>
                                          </Col>
                                        )}
                                        {showField('unitPrice') && (
                                          <Col xs={12} md={4}>
                                            <HeaderField label={t('Product Subtotal')}><Text strong>{line.unitPrice != null ? `${productSubtotal.toLocaleString('da-DK')} DKK` : <span style={{ color: '#bfbfbf' }}>—</span>}</Text></HeaderField>
                                          </Col>
                                        )}
                                        <Col xs={12} md={4}>
                                          <HeaderField label={t('Quote Total')}><Text strong>{line.unitPrice != null || line.shippingCost != null ? `${computeSupplierQuoteTotal(line, line.quantity).toLocaleString('da-DK')} DKK` : <span style={{ color: '#bfbfbf' }}>—</span>}</Text></HeaderField>
                                        </Col>
                                        {showField('productionTime') && showField('shippingTime') && (
                                          <Col xs={12} md={4}>
                                            <HeaderField label={t('Total Lead Time')}>
                                              <Text strong>{line.productionTime != null && line.shippingTime != null ? formatDays(computeTotalLeadTime(line), t) : '—'}</Text>
                                            </HeaderField>
                                          </Col>
                                        )}
                                      </Row>

                                      <Row gutter={[20, 18]} style={{ marginTop: 18 }}>
                                        {lineIndex === 0 && (
                                          <Col xs={24} md={8}>
                                            <GreyBox label={t('Compliance Info')}>
                                              {supplierCertificates.length > 0 ? (
                                                <Space wrap size={4}>
                                                  {supplierCertificates.map((certificate) => <Tag key={certificate.id} style={{ margin: 0, fontSize: 11 }}>{certificate.name}</Tag>)}
                                                </Space>
                                              ) : <span style={{ color: '#bfbfbf' }}>—</span>}
                                            </GreyBox>
                                          </Col>
                                        )}
                                        <Col xs={24} md={8}>
                                          <GreyBox label={t('Attachments')}>
                                            {line.files?.length > 0 ? <AttachmentPreview files={line.files} /> : <span style={{ color: '#bfbfbf' }}>—</span>}
                                          </GreyBox>
                                        </Col>
                                        <Col xs={24} md={8}>
                                          <GreyBox label={t('Comments')} value={line.comments} />
                                        </Col>
                                      </Row>
                                      {lineIndex < lines.length - 1 && <Divider style={{ margin: '14px 0' }} />}
                                    </div>
                                  );
                                })}
                              </Space>
                            </div>
                          );
                        })}
                      </Space>
                    )}
                  </Card>
                );
              })}
            </Space>
          )}
          {!isReadyForOffer(quote, suppliers) && (
            <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              {t('At least one supplier quote needs unit price, production time, and shipping time before this can move to Ready for Sales Quote.')}
            </Text>
          )}
        </Card>
        )}

        {activeDetailTab === 'comments' && (
        <EntityCommentsCard
          title={t('Comments to Quote')}
          entityId={quote.id}
          entityName={quote.id}
          getLatestComment={(entityId) => {
            const entityComments = (comments || []).filter((c) => c.entityId === entityId);
            return entityComments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0] || null;
          }}
          formatDateTime={formatDateTime}
          onOpenComments={(entityId, entityName) => onAddComment?.({ entityId, entityName })}
        />
        )}

        <Modal
          title={t('Connect to Sales Quote')}
          open={connectModalOpen}
          onCancel={() => { setConnectModalOpen(false); setConnectOfferId(null); }}
          onOk={() => {
            if (!connectOfferId) return;
            onConnectToOffer?.([quote.id], connectOfferId);
            setConnectModalOpen(false);
            setConnectOfferId(null);
          }}
          okText={t('Connect')}
          okButtonProps={{ disabled: !connectOfferId }}
        >
          <Select
            style={{ width: '100%' }}
            placeholder={t('Select an existing Offer')}
            value={connectOfferId}
            onChange={setConnectOfferId}
            options={eligibleOffers.map((offer) => ({ value: offer.id, label: `${offer.id} — ${offer.client}` }))}
          />
        </Modal>
      </div>
    );
  }

  const grandTotalInternal = (quote.items || []).reduce(
    (sum, item) => sum + getItemInternalTotal(item, quotations[item.id] || {}), 0,
  );
  const grandTotalCustomer = (quote.items || []).reduce(
    (sum, item) => sum + getItemCustomerTotal(item, quotations[item.id] || {}), 0,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Button icon={<HomeOutlined />} onClick={onBack}>
          {t('Back to List')}
        </Button>
        {!isConverted && (
          canConvert ? (
            <Button
              type="primary"
              icon={<SwapOutlined />}
              onClick={() => onConvertToOffer?.(quote)}
            >
              {t('Create Offer')}
            </Button>
          ) : (
            <Tooltip
              title={
                !isSales
                  ? t('Only Sales users can convert to an offer')
                  : t('Quote must be Sent to Sales Quote to create the offer')
              }
            >
              <Button icon={<SwapOutlined />} disabled>
                {t('Create Offer')}
              </Button>
            </Tooltip>
          )
        )}
      </div>

      {/* header card */}
      <Card style={{ borderRadius: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
          <Space wrap size={12} align="center">
            <Title level={2} style={{ margin: 0, fontSize: 28, lineHeight: 1.1 }}>{quote.id}</Title>
            <SoftTag tone={TYPE_TONES.Quote}>{t('Internal Quote')}</SoftTag>
            <QuoteStatusSelect
              value={quote.status}
              onChange={(val) => onUpdateQuote?.(quote.id, { status: val })}
              disabled={isConverted}
            />
          </Space>
          <Space size={8}>
            <SoftTag tone={priorityTone} style={{ fontSize: 13, padding: '2px 10px' }}>
              {t(quote.priority)}
            </SoftTag>
            {quote.urgent && (
              <SoftTag
                tone={{ color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder }}
                style={{ fontSize: 13, padding: '2px 10px' }}
              >
                {t('Urgent')}
              </SoftTag>
            )}
            {quote.reorder && (
              <SoftTag
                tone={{ color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder }}
                style={{ fontSize: 13, padding: '2px 10px' }}
              >
                {t('Re-order')}
              </SoftTag>
            )}
          </Space>
        </div>

        <Space size={16} wrap style={{ marginTop: 8 }}>
          {quote.inquiryId && (
            <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
              {t('Created from Inquiry')}:{' '}
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                onClick={() => onOpenSourceInquiry?.(quote.inquiryId)}
              >
                {quote.inquiryId}
              </Button>
            </Text>
          )}
          {quote.convertedOfferId && (
            <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
              {t('Converted to offer')}:{' '}
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                onClick={() => onOpenConvertedOffer?.(quote.convertedOfferId)}
              >
                {quote.convertedOfferId}
              </Button>
            </Text>
          )}
        </Space>

        <Divider style={{ margin: '20px 0' }} />

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={8}>
            <HeaderField label="CLIENT">
              <div style={{ fontWeight: 700, fontSize: 16 }}>{quote.client}</div>
              {lead && (
                <>
                  <div style={{ color: '#66758B', fontSize: 14 }}>{lead.phone}</div>
                  <div style={{ color: '#66758B', fontSize: 14 }}>{lead.email}</div>
                </>
              )}
            </HeaderField>
          </Col>
          <Col xs={12} sm={5}>
            <HeaderField label="SELLER">
              {quote.requestedBy || quote.createdBy || '—'}
            </HeaderField>
          </Col>
          <Col xs={12} sm={5}>
            <HeaderField label="DEADLINE">
              {quote.neededBefore ? formatDate(quote.neededBefore) : '—'}
            </HeaderField>
          </Col>
          <Col xs={12} sm={6}>
            <HeaderField label="REQUESTED DELIVERY DATE">
              {quote.requestedDeliveryDate ? formatDate(quote.requestedDeliveryDate) : '—'}
            </HeaderField>
          </Col>
          <Col xs={12} sm={5}>
            <HeaderField label="PRODUCT CATEGORY">
              {quote.productCategoryName || '—'}
            </HeaderField>
          </Col>
          <Col xs={12} sm={5}>
            <HeaderField label="REQUESTED QUANTITY">
              {quote.requestedQuantity ?? '—'}
            </HeaderField>
          </Col>
          <Col xs={12} sm={7}>
            <HeaderField label="BUDGET/TARGET PRICE (DKK)">
              {quote.budgetTargetPrice != null ? Number(quote.budgetTargetPrice).toLocaleString('da-DK') : '—'}
            </HeaderField>
          </Col>
          <Col xs={12} sm={7}>
            <HeaderField label="PRINTING/DECORATION TYPE">
              {quote.printingDecorationType || '—'}
            </HeaderField>
          </Col>
        </Row>

        {(quote.specialRequirements || quote.notes) && (
          <>
            <Divider style={{ margin: '20px 0' }} />
            <Row gutter={[24, 16]}>
              {quote.specialRequirements && (
                <Col xs={24} md={12}>
                  <HeaderField label="SPECIAL REQUIREMENTS">
                    <div
                      className="rich-text-editor-content"
                      style={{ fontSize: 14 }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(quote.specialRequirements) }}
                    />
                  </HeaderField>
                </Col>
              )}
              {quote.notes && (
                <Col xs={24} md={12}>
                  <HeaderField label="NOTES">
                    <div
                      className="rich-text-editor-content"
                      style={{ fontSize: 14 }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(quote.notes) }}
                    />
                  </HeaderField>
                </Col>
              )}
            </Row>
          </>
        )}
      </Card>

      <Tabs
        activeKey={activeDetailTab}
        onChange={setActiveDetailTab}
        items={[
          { key: 'supplierQuotes', label: t('Supplier Quotes') },
          { key: 'comments', label: t('Comments to Quote') },
        ]}
      />

      {/* Quote Products + per-product supplier/pricing work */}
      {activeDetailTab === 'supplierQuotes' && (
      <Card
        title={(
          <Space>
            <FileOutlined style={{ color: brandSemantic.primary }} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>{t('Supplier Quotes')}</span>
          </Space>
        )}
        extra={(
          <Space>
            {!isConverted && (
              <Button icon={<PlusOutlined />} onClick={() => { setEditingItem(null); setItemModalOpen(true); }}>
                {t('Add Item')}
              </Button>
            )}
            {canEdit && (
              <>
                <Button onClick={handleSaveQuotation}>{t('Save Quotation')}</Button>
                <Button type="primary" onClick={handleMarkReadyForOffer}>
                  {t('Mark Ready for Sales Quote')}
                </Button>
              </>
            )}
          </Space>
        )}
        style={{ borderRadius: 16 }}
      >
        {isConverted && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('Read-only — this quote has been converted to an offer')}
          </Text>
        )}
        <Table
          rowKey="id"
          dataSource={quote.items || []}
          columns={itemColumns}
          pagination={false}
          size="middle"
          scroll={{ x: 880 }}
          expandable={{
            expandedRowRender: renderExpandedRow,
            defaultExpandAllRows: true,
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 32, marginTop: 16, padding: '14px 20px', background: brandSemantic.layout, borderRadius: 10, border: `1px solid ${brandSemantic.border}` }}>
          <Text style={{ fontSize: 14 }}>
            {t('Grand Total — Internal')}: <strong>{grandTotalInternal.toLocaleString('da-DK')} DKK</strong>
          </Text>
          <Text style={{ fontSize: 14 }}>
            {t('Grand Total — Customer')}: <strong>{grandTotalCustomer.toLocaleString('da-DK')} DKK</strong>
          </Text>
        </div>
      </Card>
      )}

      {/* comments */}
      {activeDetailTab === 'comments' && (
      <EntityCommentsCard
        entityId={quote.id}
        entityName={quote.id}
        getLatestComment={(entityId) => {
          const entityComments = (comments || []).filter((c) => c.entityId === entityId);
          return entityComments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0] || null;
        }}
        formatDateTime={formatDateTime}
        onOpenComments={(entityId, entityName) => onAddComment?.({ entityId, entityName })}
      />
      )}

      {/* add / edit quote item modal */}
      <InquiryItemModal
        open={itemModalOpen}
        onCancel={() => { setItemModalOpen(false); setEditingItem(null); }}
        onSave={handleSaveItem}
        item={editingItem}
        categories={categories}
        products={products}
        services={services}
        logisticsItems={logisticsItems}
      />

      {/* save as new product modal */}
      <SaveAsNewProductModal
        open={saveProductOpen}
        onCancel={() => { setSaveProductOpen(false); setSaveProductItem(null); }}
        onSave={(productData) => {
          const createdProduct = onSaveNewProduct?.(productData);
          if (createdProduct && saveProductItem) {
            onUpdateQuote?.(quote.id, {
              items: (quote.items || []).map((item) => (
                item.id === saveProductItem.id
                  ? {
                      ...item,
                      productId: createdProduct.id,
                      categoryId: createdProduct.categoryId,
                      categoryName: categories.find((category) => category.id === createdProduct.categoryId)?.name || item.categoryName,
                      productName: createdProduct.name,
                      isNewProduct: false,
                    }
                  : item
              )),
            });
          }
          setSaveProductOpen(false);
          setSaveProductItem(null);
        }}
        initialName={saveProductItem?.productName || ''}
        categories={categories}
        suppliers={suppliers}
        existingProducts={products}
      />
    </div>
  );
}
